import { isJobAppLinkRoute, type AppLinkRoute } from '../shared/app-links';

/**
 * CapturePoint's `capturepoint://` links (CPv6 639c2452, docs/product/app-links.md), in the browser. The
 * desktop's links open the installed app from a web page or an email and raise the window event the
 * app's own button raises (`renderer/app-link-routing.ts`). A browser has no app to open, so Paige's
 * links are its own addresses, handed here through the bridge (`window.paigeWorkbench.appLink`):
 *
 * - `tour`: the new-job route with `?tour=1` (the jobs home's welcome, and any email that says "take
 *   the tour"); a tour already under way is resumed (startTour), as a second click on the desktop's
 *   link does.
 * - the job links: a job's review route with `?open=` (`results`, `teach`, `lookups`, `thresholds`;
 *   apps/web review-search.ts). The address names the job, so the App opens that job's place, the
 *   way its own buttons do (App.tsx's PAIGE seam in `followAppLinkRef`): Learning at Results,
 *   Documents on Teach, Job setup at Lookup lists or at Review thresholds. Learning's Train is the
 *   desktop's training, hidden in Paige (`paige-host.ts`), so `job/learning/train` opens Results.
 *
 * The settings links (License, Performance) are the desktop's own screens and are not followed here.
 * The App listens once its jobs are loaded, and the route is given to it once per bridge, so a
 * re-render does not open it twice.
 */
const delivered = new WeakSet<object>();

/** What a Paige address may ask for: the tour and the job places, never the desktop's settings. */
export function paigeAppLinkRoute(value: unknown): AppLinkRoute | null {
  if (value === 'tour') return 'tour';
  if (!isJobAppLinkRoute(value)) return null;
  return value === 'job/learning/train' ? 'job/learning/results' : value;
}

export function browserAppLinks(): Record<string, unknown> {
  return {
    onAppLink: (listener: (route: AppLinkRoute) => void) => {
      const bridge = typeof window === 'undefined' ? undefined : window.paigeWorkbench;
      const route = paigeAppLinkRoute(bridge?.appLink);
      if (!bridge || !route || delivered.has(bridge)) return () => undefined;
      // Marked when it is handed over, not when asked for: a listener that is set up and torn down at once (a
      // development double render) must not use up the link before the one that stays hears it.
      const timer = window.setTimeout(() => {
        if (delivered.has(bridge)) return;
        delivered.add(bridge);
        listener(route);
      }, 0);
      return () => window.clearTimeout(timer);
    },
  };
}
